import React, { useState, useEffect } from "react";
import { Users, Star, Phone, Envelope, Tag, Note, Clock, X, Plus } from "@phosphor-icons/react";
import { api, formatCurrency, formatDate } from "../../utils/api";
import { Button } from "../shared/Button";
import { Badge } from "../shared/Badge";
import { Loading, EmptyState } from "../shared/EmptyState";
import { Modal } from "../shared/Modal";
import { Input, Textarea } from "../shared/Input";

export const CRMClientes = ({ adminToken }) => {
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState("todos");
  const [modalDetalle, setModalDetalle] = useState(false);
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [modalNota, setModalNota] = useState(false);
  const [nuevaNota, setNuevaNota] = useState("");
  const [nuevoTag, setNuevoTag] = useState("");

  useEffect(() => {
    cargarClientes();
  }, []);

  const cargarClientes = async () => {
    try {
      const data = await api.get("/clientes", adminToken);
      setClientes(data);
    } catch (e) {
      console.error("Error:", e);
    } finally {
      setLoading(false);
    }
  };

  const cambiarReputacion = async (id, reputacion) => {
    try {
      await api.put(`/clientes/${id}`, { reputacion }, adminToken);
      cargarClientes();
      if (clienteSeleccionado?.id === id) {
        setClienteSeleccionado({ ...clienteSeleccionado, reputacion });
      }
    } catch (e) {
      console.error("Error:", e);
    }
  };

  const agregarNota = async () => {
    if (!nuevaNota.trim() || !clienteSeleccionado) return;
    try {
      const nota = {
        texto: nuevaNota,
        fecha: new Date().toISOString(),
        autor: "Admin"
      };
      const notasActualizadas = [...(clienteSeleccionado.notas || []), nota];
      await api.put(`/clientes/${clienteSeleccionado.id}`, { notas: notasActualizadas }, adminToken);
      setClienteSeleccionado({ ...clienteSeleccionado, notas: notasActualizadas });
      setNuevaNota("");
      setModalNota(false);
      cargarClientes();
    } catch (e) {
      console.error("Error:", e);
    }
  };

  const agregarTag = async () => {
    if (!nuevoTag.trim() || !clienteSeleccionado) return;
    try {
      const tagsActualizados = [...(clienteSeleccionado.tags || []), nuevoTag.trim()];
      await api.put(`/clientes/${clienteSeleccionado.id}`, { tags: tagsActualizados }, adminToken);
      setClienteSeleccionado({ ...clienteSeleccionado, tags: tagsActualizados });
      setNuevoTag("");
      cargarClientes();
    } catch (e) {
      console.error("Error:", e);
    }
  };

  const eliminarTag = async (tag) => {
    if (!clienteSeleccionado) return;
    try {
      const tagsActualizados = (clienteSeleccionado.tags || []).filter(t => t !== tag);
      await api.put(`/clientes/${clienteSeleccionado.id}`, { tags: tagsActualizados }, adminToken);
      setClienteSeleccionado({ ...clienteSeleccionado, tags: tagsActualizados });
      cargarClientes();
    } catch (e) {
      console.error("Error:", e);
    }
  };

  const bloquearCliente = async (cliente) => {
    const nuevoEstado = !cliente.bloqueado;
    const confirmacion = window.confirm(
      `¿${nuevoEstado ? 'Bloquear' : 'Desbloquear'} a ${cliente.nombre}?`
    );
    if (!confirmacion) return;
    
    try {
      await api.put(`/clientes/${cliente.id}/bloquear`, { bloqueado: nuevoEstado }, adminToken);
      cargarClientes();
      if (modalDetalle) {
        setClienteSeleccionado({ ...clienteSeleccionado, bloqueado: nuevoEstado });
      }
    } catch (e) {
      console.error("Error:", e);
      alert("Error al actualizar cliente");
    }
  };

  const eliminarCliente = async (cliente) => {
    const confirmacion = window.confirm(
      `¿Eliminar permanentemente a ${cliente.nombre}? Esta acción no se puede deshacer.`
    );
    if (!confirmacion) return;
    
    try {
      await api.delete(`/clientes/${cliente.id}`, adminToken);
      cargarClientes();
      setModalDetalle(false);
      setClienteSeleccionado(null);
    } catch (e) {
      console.error("Error:", e);
      alert("Error al eliminar cliente");
    }
  };

  const verDetalle = async (cliente) => {
    try {
      // Cargar historial completo del cliente
      const historial = await api.get(`/clientes/${cliente.id}/historial`, adminToken);
      setClienteSeleccionado({ ...cliente, historial });
      setModalDetalle(true);
    } catch (e) {
      console.error("Error:", e);
      setClienteSeleccionado(cliente);
      setModalDetalle(true);
    }
  };

  const clientesFiltrados = clientes.filter(c => {
    if (filtro === "todos") return true;
    if (filtro === "top") return c.total_gastado > 5000;
    if (filtro === "recurrentes") return c.total_presupuestos > 1;
    return c.reputacion === filtro;
  });

  if (loading) return <Loading />;

  return (
    <div className="pb-24">
      <div className="container pt-4">
        <div className="mb-4">
          <h1 className="text-h2 font-display mb-1">👥 CRM Clientes</h1>
          <p className="text-small text-secondary">{clientes.length} clientes registrados</p>
        </div>

        {/* Filtros */}
        <div className="tabs mb-4">
          {[
            { id: "todos", label: "Todos" },
            { id: "top", label: "🏆 TOP" },
            { id: "recurrentes", label: "🔄 Recurrentes" },
            { id: "bueno", label: "⭐ Buenos" },
            { id: "regular", label: "🟡 Regulares" },
            { id: "malo", label: "🔴 Problemáticos" },
          ].map(f => (
            <button key={f.id} onClick={() => setFiltro(f.id)} className={`tab ${filtro === f.id ? "tab-active" : ""}`}>
              {f.label}
            </button>
          ))}
        </div>

        {clientesFiltrados.length === 0 ? (
          <EmptyState icon="👥" title="Sin clientes" description="Los clientes se registran automáticamente al crear presupuestos" />
        ) : (
          <div className="space-y-3">
            {clientesFiltrados.map(cliente => (
              <div 
                key={cliente.id} 
                className="card p-4 cursor-pointer card-interactive"
                onClick={() => verDetalle(cliente)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="font-bold text-small">{cliente.nombre}</div>
                    <div className="text-tiny text-secondary flex items-center gap-2 mt-1">
                      <Phone size={12} /> {cliente.telefono}
                    </div>
                    {cliente.tags && cliente.tags.length > 0 && (
                      <div className="flex gap-1 mt-2">
                        {cliente.tags.slice(0, 3).map((tag, i) => (
                          <Badge key={i} variant="outline" style={{ fontSize: "0.7rem" }}>
                            {tag}
                          </Badge>
                        ))}
                        {cliente.tags.length > 3 && (
                          <Badge variant="outline" style={{ fontSize: "0.7rem" }}>
                            +{cliente.tags.length - 3}
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {["bueno", "regular", "malo"].map(rep => (
                      <button
                        key={rep}
                        onClick={(e) => {
                          e.stopPropagation();
                          cambiarReputacion(cliente.id, rep);
                        }}
                        style={{
                          fontSize: "1.2rem",
                          opacity: cliente.reputacion === rep ? 1 : 0.3,
                          cursor: "pointer",
                          border: "none",
                          background: "none"
                        }}
                      >
                        {rep === "bueno" ? "⭐" : rep === "regular" ? "🟡" : "🔴"}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex gap-4 text-tiny">
                  <div>
                    <span className="text-secondary">Presupuestos:</span> <span className="font-bold">{cliente.total_presupuestos || 0}</span>
                  </div>
                  <div>
                    <span className="text-secondary">Total gastado:</span> <span className="font-bold" style={{ color: "var(--brand-accent)" }}>{formatCurrency(cliente.total_gastado || 0)}</span>
                  </div>
                </div>
                {cliente.ultima_obra && (
                  <div className="text-tiny text-secondary mt-2">Última obra: {cliente.ultima_obra}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal Detalle Cliente */}
      <Modal 
        isOpen={modalDetalle} 
        onClose={() => setModalDetalle(false)} 
        title={`Cliente: ${clienteSeleccionado?.nombre || ""}`}
      >
        {clienteSeleccionado && (
          <div className="space-y-4">
            {/* Info básica */}
            <div className="card p-4" style={{ background: "var(--surface-secondary)" }}>
              <div className="flex items-center gap-2 mb-2">
                <Phone size={16} />
                <span className="text-small">{clienteSeleccionado.telefono}</span>
              </div>
              {clienteSeleccionado.email && (
                <div className="flex items-center gap-2">
                  <Envelope size={16} />
                  <span className="text-small">{clienteSeleccionado.email}</span>
                </div>
              )}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-3">
              <div className="card p-3 text-center">
                <div className="text-h3 font-display" style={{ color: "var(--brand-accent)" }}>
                  {clienteSeleccionado.total_presupuestos || 0}
                </div>
                <div className="text-tiny text-secondary">Presupuestos</div>
              </div>
              <div className="card p-3 text-center">
                <div className="text-h3 font-display" style={{ color: "var(--brand-accent)" }}>
                  {formatCurrency(clienteSeleccionado.total_gastado || 0)}
                </div>
                <div className="text-tiny text-secondary">Total Gastado</div>
              </div>
            </div>

            {/* Tags */}
            <div>
              <div className="text-label mb-2">TAGS</div>
              <div className="flex gap-2 mb-2 flex-wrap">
                {(clienteSeleccionado.tags || []).map((tag, i) => (
                  <div key={i} className="flex items-center gap-1 px-2 py-1 rounded-lg text-tiny" style={{ background: "var(--surface-secondary)" }}>
                    <Tag size={12} />
                    <span>{tag}</span>
                    <button onClick={() => eliminarTag(tag)} className="ml-1">
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder="Nuevo tag..."
                  value={nuevoTag}
                  onChange={(e) => setNuevoTag(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && agregarTag()}
                />
                <Button variant="outline" size="sm" onClick={agregarTag} disabled={!nuevoTag.trim()}>
                  Agregar
                </Button>
              </div>
            </div>

            {/* Notas */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-label">NOTAS</div>
                <Button variant="ghost" size="sm" onClick={() => setModalNota(true)}>
                  <Plus size={16} />
                </Button>
              </div>
              <div className="space-y-2">
                {(clienteSeleccionado.notas || []).slice(-3).reverse().map((nota, i) => (
                  <div key={i} className="card p-3" style={{ background: "var(--surface-secondary)" }}>
                    <div className="text-small mb-1">{nota.texto}</div>
                    <div className="text-tiny text-secondary">
                      {nota.autor} • {formatDate(nota.fecha)}
                    </div>
                  </div>
                ))}
                {(!clienteSeleccionado.notas || clienteSeleccionado.notas.length === 0) && (
                  <div className="text-center text-tiny text-secondary py-4">
                    Sin notas
                  </div>
                )}
              </div>
            </div>

            {/* Historial */}
            {clienteSeleccionado.historial && clienteSeleccionado.historial.length > 0 && (
              <div>
                <div className="text-label mb-2">HISTORIAL</div>
                <div className="space-y-2">
                  {clienteSeleccionado.historial.slice(-5).reverse().map((item, i) => (
                    <div key={i} className="flex items-start gap-2 text-tiny">
                      <Clock size={14} className="mt-0.5" style={{ color: "var(--text-secondary)" }} />
                      <div>
                        <div>{item.accion}</div>
                        <div className="text-secondary">{formatDate(item.fecha)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* Acciones */}
            <div className="flex gap-2 pt-4 border-t border-subtle">
              <Button
                variant={clienteSeleccionado.bloqueado ? "outline" : "danger"}
                className="btn-full"
                onClick={() => bloquearCliente(clienteSeleccionado)}
              >
                {clienteSeleccionado.bloqueado ? "🔓 Desbloquear" : "🔒 Bloquear"}
              </Button>
              <Button
                variant="ghost"
                onClick={() => eliminarCliente(clienteSeleccionado)}
              >
                🗑️ Eliminar
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal Agregar Nota */}
      <Modal isOpen={modalNota} onClose={() => setModalNota(false)} title="Agregar Nota">
        <div>
          <Textarea
            label="Nota"
            placeholder="Escribe una nota sobre este cliente..."
            value={nuevaNota}
            onChange={(e) => setNuevaNota(e.target.value)}
            rows={4}
          />
          <Button
            variant="accent"
            className="btn-full mt-4"
            onClick={agregarNota}
            disabled={!nuevaNota.trim()}
          >
            Guardar Nota
          </Button>
        </div>
      </Modal>
    </div>
  );
};